import { AuthorId, CorpusId } from '../models/aliases';
import { defineEvent } from './common';
import { ExportFormat } from '../utils/paper-formatting';

const PREFIX = 'paper_finder_widget' as const;

export const events = {
  selection: {
    ClearAllClicked: defineEvent()(`${PREFIX}__clear_all_selection`),
    PaperSelectionChanged: defineEvent<SelectedPerPaperProps>()(
      `${PREFIX}__paper_select`,
    ),
  },
  facets: {
    ClearAllClicked: defineEvent()(`${PREFIX}__clear_filters`),
    FacetSelectionChanged: defineEvent<FacetSelectionProps>()(
      `${PREFIX}__facet_select`,
    ),
  },
  history: {
    UndoClicked: defineEvent()(`${PREFIX}__undo_btn`),
    RedoClicked: defineEvent()(`${PREFIX}__redo_btn`),
  },
  pagination: {
    PageSelectionChanged: defineEvent<PageProp>()(`${PREFIX}__page_select`),
  },
  navigation: {
    PaperLinkClicked: defineEvent<CorpusIdProp>()(`${PREFIX}__paper_link_btn`),
    PaperLegalLinkClicked: defineEvent<CorpusIdProp>()(
      `${PREFIX}__legal_link_btn`,
    ),
    PaperAuthorClicked: defineEvent<CorpusIdAndAuthorIdProp>()(
      `${PREFIX}__author_link_btn`,
    ),
  },
  sort: {
    DirectionToggleClicked: defineEvent<SortDirectionProp>()(
      `${PREFIX}__sort_direction_btn`,
    ),
    SortByChanged: defineEvent<SortByProp>()(`${PREFIX}__sort_select`),
  },
  panels: {
    ShowMobileFacetPanelChanged: defineEvent<IsExpandedProp>()(
      `${PREFIX}__mobile_facets_pnl`,
    ),
    ShowFacetChanged: defineEvent<FacetAccordionProps>()(
      `${PREFIX}__facet_pnl`,
    ),
    ShowEvidenceAbstractChanged: defineEvent<PaperAccordionProps>()(
      `${PREFIX}__paper_abstract_pnl`,
    ),
    ShowEvidenceSnippetsChanged: defineEvent<PaperAccordionProps>()(
      `${PREFIX}__paper_snippets_pnl`,
    ),
    ShowEvidenceCitationsChanged: defineEvent<PaperAccordionProps>()(
      `${PREFIX}__paper_citation_pnl`,
    ),
    ShowEvidenceViewChanged: defineEvent<PaperAccordionProps>()(
      `${PREFIX}__paper_evidence_btn`,
    ),
  },
  export: {
    ExportCitationClicked: defineEvent<CorpusIdProp>()(
      `${PREFIX}__copy_citation_btn`,
    ),
    ExportFilteredClicked: defineEvent()(`${PREFIX}__download_btn`),
    ExportSelectedClicked: defineEvent()(`${PREFIX}__download_selected_btn`),
    ExportDialogCancelClicked: defineEvent()(`${PREFIX}__download_cancel_btn`),
    ExportDialogExportClicked: defineEvent<ExportFormatProp>()(
      `${PREFIX}__download_start_btn`,
    ),
    ExportDialogFormatChanged: defineEvent<ExportFormatProp>()(
      `${PREFIX}__download_select_fmt`,
    ),
  },
  feedback: {
    ThumbsUpClicked: defineEvent<SelectedPerPaperProps>()(
      `${PREFIX}__thumbs_up_btn`,
    ),
    ThumbsDownClicked: defineEvent<SelectedPerPaperProps>()(
      `${PREFIX}__thumbs_down_btn`,
    ),
    TextFeedbackClicked: defineEvent<CorpusIdProp>()(
      `${PREFIX}__text_feedback_btn`,
    ),
    CloseTextFeedbackClicked: defineEvent<CorpusIdProp>()(
      `${PREFIX}__feedback_close_btn`,
    ),
    SubmitTextFeedbackClicked: defineEvent<FeedbackTextParams>()(
      `${PREFIX}__feedback_send_btn`,
    ),
    TextFeedbackFocused: defineEvent<CorpusIdProp>()(
      `${PREFIX}__feedback_text_box`,
    ),
  },
};

interface PageProp {
  page: number;
}

interface CorpusIdProp {
  corpusId: CorpusId;
}

interface CorpusIdAndAuthorIdProp extends CorpusIdProp {
  authorId: AuthorId;
}

interface FeedbackTextParams extends CorpusIdProp {
  message: string;
}

interface SortDirectionProp {
  isDescending: boolean;
}

interface SortByProp {
  sortBy: string;
}

interface SelectedPerPaperProps extends CorpusIdProp {
  isSelected: boolean;
}

interface ExportFormatProp {
  formatType: ExportFormat;
}

interface IsExpandedProp {
  isExpanded: boolean;
}

interface PaperAccordionProps extends CorpusIdProp {
  isExpanded: boolean;
}

interface FacetAccordionProps {
  panelName: string;
  isExpanded: boolean;
}

interface FacetSelectionProps {
  facetName: string;
  value: string;
  isSelected: boolean;
}
